import React, { useState, useEffect } from 'react';

/**
 * Détecte iOS Safari et affiche un banner "Ajouter à l'écran d'accueil"
 * Ne s'affiche que si:
 * 1. L'utilisateur est sur iOS (iPhone/iPad)
 * 2. L'app n'est PAS déjà installée (standalone)
 * 3. Le banner n'a pas déjà été fermé récemment
 */
export default function IOSInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [step, setStep] = useState(0); // 0 = banner, 1 = étape 1, 2 = étape 2

  useEffect(() => {
    // Détecte iOS
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    // Détecte si déjà installé en mode standalone
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    // Vérifie si l'utilisateur a déjà fermé le banner
    const dismissed = localStorage.getItem('ios-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const threeDays = 3 * 24 * 60 * 60 * 1000;

    if (isIOS && !isStandalone && (Date.now() - dismissedTime > threeDays)) {
      // Petit délai pour ne pas gêner l'utilisateur dès l'ouverture
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('ios-install-dismissed', Date.now().toString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      <style>{`
        .ios-install-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 99998;
          animation: iosFadeIn 0.3s ease;
        }

        .ios-install-banner {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          border-radius: 20px 20px 0 0;
          padding: 20px;
          padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
          z-index: 99999;
          box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
          animation: iosSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes iosSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        @keyframes iosFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .ios-banner-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .ios-banner-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ios-banner-title h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .ios-banner-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: linear-gradient(135deg, #F54119, #FF5500);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }

        .ios-banner-close {
          background: #f1f1f1;
          border: none;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          font-size: 18px;
          color: #666;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ios-banner-description {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 16px;
          line-height: 1.4;
        }

        .ios-banner-steps {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }

        .ios-step {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 12px;
          transition: all 0.3s;
        }

        .ios-step.active {
          background: #FEE9E2;
          border: 1.5px solid #F54119;
        }

        .ios-step-number {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #e0e0e0;
          color: #666;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .ios-step.active .ios-step-number {
          background: #F54119;
          color: white;
        }

        .ios-step-text {
          font-size: 0.9rem;
          color: #333;
        }

        .ios-step-text .share-icon {
          display: inline-block;
          vertical-align: middle;
          font-size: 1.1rem;
        }

        .ios-step-text strong {
          color: #1a1a1a;
        }

        .ios-banner-button {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #F54119, #FF5500);
          color: white;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
        }

        .ios-banner-skip {
          text-align: center;
          margin-top: 10px;
        }

        .ios-banner-skip button {
          background: none;
          border: none;
          color: #999;
          font-size: 0.85rem;
          cursor: pointer;
          text-decoration: underline;
          font-family: 'Poppins', sans-serif;
        }

        .ios-arrow-down {
          text-align: center;
          margin-top: 8px;
          animation: iosBounce 1.5s infinite;
        }

        @keyframes iosBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
      `}</style>

      <div className="ios-install-overlay" onClick={handleDismiss} />
      
      <div className="ios-install-banner">
        <div className="ios-banner-header">
          <div className="ios-banner-title">
            <div className="ios-banner-icon"><img src="/logo192.png" alt="" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} /></div>
            <h3>Installer Mbiyo</h3>
          </div>
          <button className="ios-banner-close" onClick={handleDismiss}>✕</button>
        </div>

        <p className="ios-banner-description">
          Installez l'application sur votre iPhone pour un accès rapide depuis l'écran d'accueil !
        </p>

        <div className="ios-banner-steps">
          <div className={`ios-step ${step === 0 ? 'active' : ''}`}>
            <div className="ios-step-number">1</div>
            <div className="ios-step-text">
              Appuyez sur <strong>•••</strong> (trois points) en bas à droite de Safari
            </div>
          </div>

          <div className={`ios-step ${step === 1 ? 'active' : ''}`}>
            <div className="ios-step-number">2</div>
            <div className="ios-step-text">
              Appuyez sur <span className="share-icon">📤</span> <strong>Partager</strong>
            </div>
          </div>

          <div className={`ios-step ${step === 2 ? 'active' : ''}`}>
            <div className="ios-step-number">3</div>
            <div className="ios-step-text">
              Défilez et appuyez sur <strong>"Sur l'écran d'accueil"</strong> ➕
            </div>
          </div>

          <div className={`ios-step ${step === 3 ? 'active' : ''}`}>
            <div className="ios-step-number">4</div>
            <div className="ios-step-text">
              Appuyez sur <strong>"Ajouter"</strong> en haut à droite ✅
            </div>
          </div>
        </div>

        <button 
          className="ios-banner-button" 
          onClick={() => {
            if (step < 3) {
              setStep(step + 1);
            } else {
              handleDismiss();
            }
          }}
        >
          {step < 3 ? 'Étape suivante →' : "C'est compris ! ✅"}
        </button>

        <div className="ios-banner-skip">
          <button onClick={handleDismiss}>Plus tard</button>
        </div>

        {step === 0 && (
          <div className="ios-arrow-down">
            <span style={{ fontSize: '1.5rem' }}>👇</span>
          </div>
        )}
      </div>
    </>
  );
}
